import { openAPIClient } from '../openApiClient';
import type {
  CreateGlossaryEntryResponse,
  CreateGlossaryEntryServiceConfig,
  DeleteGlossaryEntryServiceConfig,
  GetGlossaryEntryServiceConfig,
  GlossaryEntryResponse,
  GlossaryListResponse,
  GlossaryListServiceConfig,
  UpdateGlossaryEntryResponse,
  UpdateGlossaryEntryServiceConfig,
} from './types';

/**
 * Gets glossary entries for a space
 * Used in: GlossaryTable for displaying the list of glossary entries
 */
export async function getGlossaryListService({
  httpRequestConfig,
  params,
}: GlossaryListServiceConfig): Promise<GlossaryListResponse> {
  const client = await openAPIClient(httpRequestConfig);

  const response = await client.GET('/v1/auth/glossary/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
      query: params?.query,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Gets a single glossary entry by ID
 * Used for glossary entry detail views
 */
export async function getGlossaryEntryService({
  httpRequestConfig,
  params,
}: GetGlossaryEntryServiceConfig): Promise<GlossaryEntryResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/glossary/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Creates a new glossary entry
 * Used in glossary entry creation forms
 */
export async function createGlossaryEntryService({
  httpRequestConfig,
  params,
}: CreateGlossaryEntryServiceConfig): Promise<CreateGlossaryEntryResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/glossary/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  if (response.data === undefined || response.data === null) {
    throw new Error('Glossary entry creation failed - no data returned');
  }

  return response.data;
}

/**
 * Updates an existing glossary entry
 * Used in glossary entry editing forms
 */
export async function updateGlossaryEntryService({
  httpRequestConfig,
  params,
}: UpdateGlossaryEntryServiceConfig): Promise<UpdateGlossaryEntryResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.PUT('/v1/auth/glossary/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Deletes a glossary entry
 * Used in glossary entry deletion confirmations
 */
export async function deleteGlossaryEntryService({
  httpRequestConfig,
  params,
}: DeleteGlossaryEntryServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.DELETE('/v1/auth/glossary/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}
